import { formatDate } from '@frontend/mwm-ui'
import { useReducer, useState, useCallback } from 'react'

import type { ReactElement } from 'react'

import Button from '~/components/Buttons/Button'
import CollapseButton from '~/components/Buttons/CollapseButton'
import Filters, { FiltersContent } from '~/components/Filters'
import DateInput from '~/components/Inputs/Basic/DateInput'
import MultiSelectInput from '~/components/Inputs/Basic/MultiSelectInput'
import { CustomReportStatusBadgeOption } from '~/components/SelectOptions/CustomBadgeOption'
import {
  DEFAULT_DATE_RANGE,
  MAX_DAYS_RANGE,
  REPORT_REASON_MAPPING,
  REPORT_STATUS_MAPPING,
  REPORT_TYPE_MAPPING,
  TODAY,
} from '~/const'

type ActionType = 'types' | 'reasons' | 'start' | 'end' | 'reviewers' | 'statuses' | 'reset'

type Actions = {
  type: ActionType
  value?: string[]
}

export interface FiltersState extends Record<string, string[] | string> {
  end: string
  reasons: string[]
  reviewers: string[]
  start: string
  statuses: string[]
  types: string[]
}

type ReducerState = (state: FiltersState, action: Actions) => FiltersState

const reducer = (state: FiltersState, action: Actions) => {
  switch (action.type) {
    case 'start':
      const startDate = (action.value && action.value[0]) || ''
      return {
        ...state,
        start: startDate,
      }
    case 'end':
      const endDate = (action.value && action.value[0]) || ''
      return {
        ...state,
        end: endDate,
      }
    case 'types':
      return { ...state, types: action.value || [] }
    case 'reasons':
      return { ...state, reasons: action.value || [] }
    case 'reviewers':
      return { ...state, reviewers: action.value || [] }
    case 'statuses':
      return { ...state, statuses: action.value || [] }
    case 'reset':
      return initialState
    default:
      return state
  }
}

export const initialState: FiltersState = {
  end: DEFAULT_DATE_RANGE.end,
  reasons: [],
  reviewers: [],
  start: DEFAULT_DATE_RANGE.start,
  statuses: [],
  types: [],
}

const getOptionFromFilters = (filterValues: string[], options: IOption[]): IOption[] => {
  return options.filter(({ value }) => filterValues.includes(value))
}

interface Props {
  onFiltersChange: (filter: string, values: string[]) => void
  onResetFilters: VoidFunction
}

const ReportsFilters = ({ onFiltersChange, onResetFilters }: Props): ReactElement => {
  /** Local state */

  const [state, dispatch] = useReducer<ReducerState>(reducer, initialState)

  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false)

  /** Queries & Mutations */

  /** Handlers */

  const toggleCollapseFilters = () => setIsFiltersCollapsed(!isFiltersCollapsed)

  const handleFilterChanged = (filter: ActionType, values: string[]) => {
    dispatch({ type: filter, value: values })
    onFiltersChange(filter, values)
  }

  const handleResetAll = useCallback(() => {
    dispatch({ type: 'reset' })
    onResetFilters()
  }, [dispatch, onResetFilters])

  const alphabeticalSorter = (a: IOption, b: IOption) => (a.label > b.label ? 1 : -1)

  const typeOptions: IOption[] = Object.entries(REPORT_TYPE_MAPPING)
    .map(([key, value]) => ({
      label: value,
      value: key,
    }))
    .sort(alphabeticalSorter)

  const reasonsOptions: IOption[] = Object.entries(REPORT_REASON_MAPPING)
    .map(([key, value]) => ({
      label: value,
      value: key,
    }))
    .sort(alphabeticalSorter)

  const statusesOptions: IOption[] = Object.entries(REPORT_STATUS_MAPPING)
    .map(([key, value]) => ({
      label: value,
      value: key,
    }))
    .sort(alphabeticalSorter)

  return (
    <Filters className="reports-filters">
      {!isFiltersCollapsed ? (
        <FiltersContent>
          <div className="filters-content-container">
            <MultiSelectInput
              label="Types"
              onChange={(value: unknown) =>
                handleFilterChanged(
                  'types',
                  (value as IOption[]).map(({ value }) => value)
                )
              }
              options={typeOptions}
              placeholder="All Types"
              value={getOptionFromFilters(state.types, typeOptions)}
            />
            <MultiSelectInput
              label="Reasons"
              onChange={(value: unknown) =>
                handleFilterChanged(
                  'reasons',
                  (value as IOption[]).map(({ value }) => value)
                )
              }
              options={reasonsOptions}
              placeholder="All Reasons"
              value={getOptionFromFilters(state.reasons, reasonsOptions)}
            />
            <MultiSelectInput
              components={{ Option: CustomReportStatusBadgeOption }}
              label="Statuses"
              onChange={(value: unknown) =>
                handleFilterChanged(
                  'statuses',
                  (value as IOption[]).map(({ value }) => value)
                )
              }
              options={statusesOptions}
              placeholder="All Statuses"
              value={getOptionFromFilters(state.statuses, statusesOptions)}
            />
            <DateInput
              endDate={state.end}
              maxDate={TODAY}
              maxRangeDays={MAX_DAYS_RANGE}
              onDateRangeChange={(value: unknown) => {
                const [start, end] = value as string[]
                handleFilterChanged('start', [formatDate(start, 'YYYY-MM-DD')])
                handleFilterChanged('end', [formatDate(end, 'YYYY-MM-DD')])
              }}
              startDate={state.start}
              withFilters
              withRange
            />
          </div>
          <div>
            <Button className="reset-button" ghost onClick={handleResetAll}>
              Reset all
            </Button>
          </div>
        </FiltersContent>
      ) : null}
      <CollapseButton isCollapsed={isFiltersCollapsed} onClick={toggleCollapseFilters} />
    </Filters>
  )
}

export default ReportsFilters
